import React, { useState, useEffect, useContext, useCallback } from "react";
import API from "../../../services/interceptor";
import Loading from "../../Loading";
import { AuthContext } from "../../../context/AuthContext";
import PatientReportPDF from "../../Dashboards/Doctor/PatientReportPDF";
import {
  FileText,
  Calendar,
  User as UserIcon,
  Activity,
  ClipboardList,
  Heart,
  Eye,
  Stethoscope,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Pill,
} from "lucide-react";
import { Link } from "react-router-dom";
import MyReportHTML from "./MyReportHTML.js";

const MedicalOPDDetails = () => {
  const {
    loginUser,
    getUserDetails,
    userDetails,
    userDetailsLoading,
    userDetailsError,
  } = useContext(AuthContext);

  const [expandedRecords, setExpandedRecords] = useState({});

  const toggleRecord = useCallback((id) => {
    setExpandedRecords((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Fetch existing medical details on component mount
  useEffect(() => {
    if (!userDetails) {
      getUserDetails(loginUser.id);
    }
  }, [userDetails, getUserDetails, loginUser.id]);

  // Helper function to format date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }, []);

  // Helper function to check if data exists and is not empty
  const hasData = useCallback((data) => {
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0;
    if (typeof data === "object") return Object.keys(data).length > 0;
    return data !== null && data !== undefined && data !== "";
  }, []);

  if (userDetailsLoading) {
    return <Loading />;
  }

  if (userDetailsError || !userDetails) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6">
        <div className="mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Medical Records
            </h1>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="text-center py-8">
              <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
              <h3 className="text-lg font-semibold text-red-600 mb-2">
                Error Loading Medical Data
              </h3>
              <p className="text-red-500">
                Unable to fetch your medical records. Please try again later.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if opdForms exists and has data
  if (!userDetails.opdForms || userDetails.opdForms.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6">
        <div className=" mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Medical Records
            </h1>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="text-center py-8">
              <FileText size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">
                No Medical Records Found
              </h3>
              <p className="text-gray-500">
                You don't have any medical records yet. Visit our clinic to
                start your medical journey.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Medical Records
          </h1>
          <p className="text-gray-600">
            Your medical history and treatment records
          </p>
        </div>

        {/* Records List */}
        <div className="space-y-4">
          {userDetails.opdForms.map((opdForm) => {
            const {
              opdNumber,
              checkupInfo,
              patientReport,
              createdAt,
              additionalNotes,
            } = opdForm;

            const examinationFindings =
              patientReport?.examinationFindings || null;
            const diagnosis = patientReport?.diagnosis || null;
            const treatmentPlan = patientReport?.treatmentPlan || null;
            const documents = patientReport?.documents || null;

            const isExpanded = expandedRecords[opdForm._id];

            return (
              <div
                key={opdForm._id}
                className="bg-white rounded-lg shadow-sm border"
              >
                {/* Record Summary */}
                <div className="p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex  gap-3 mb-2">
                        <div className=" p-2 rounded-lg">
                          <ClipboardList className="h-5 w-5 text-blue-600 text-xl" />
                        </div>
                        <div>
                          <h3 className="text-lg md:text-xl font-semibold text-gray-900">
                            OPD: {opdNumber || "Not specified"}
                          </h3>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(createdAt)}
                          </p>
                          {checkupInfo && (
                            <div className="flex items-center gap-2 mt-2">
                              <Stethoscope className="h-4 w-4 text-green-600" />
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">
                                  Checkup For:
                                </span>{" "}
                                <span className="text-green-700">
                                  {checkupInfo}
                                </span>
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <MyReportHTML
                      patientReport={patientReport}
                      opdForm={opdForm}
                      userDetails={userDetails}
                      btnTitle="View More"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MedicalOPDDetails;
